import { useEffect, useState } from "react";
import { Building2, Plus, Save, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

const EMPTY = { name: "", manager_email: "", member_emails: "", seats: 5, course_ids: [], notes: "" };

const toForm = (o) => ({ ...o, member_emails: (o.member_emails || []).join("\n") });
const toPayload = (f) => ({ ...f, seats: Number(f.seats) || 0, member_emails: f.member_emails.split(/[\n,;]+/).map((e) => e.trim()).filter(Boolean) });

export const OrganizationsAdmin = () => {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const reload = () => api.get("/admin/organizations").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => {
    reload();
    api.get("/academy/courses").then((r) => setCourses(r.data.filter((c) => c.price > 0))).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.id) await api.put(`/admin/organizations/${form.id}`, toPayload(form));
      else await api.post("/admin/organizations", toPayload(form));
      toast.success(t("orgs.saved"));
      setForm(EMPTY);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/organizations/${id}`);
    reload();
  };

  const toggleCourse = (id) => setForm((f) => ({ ...f, course_ids: f.course_ids.includes(id) ? f.course_ids.filter((x) => x !== id) : [...f.course_ids, id] }));

  return (
    <div className="mt-10 grid lg:grid-cols-[1fr_1.2fr] gap-8" data-testid="admin-orgs-tab">
      <form onSubmit={save} className="card-dark p-6 space-y-4 h-fit" data-testid="org-form">
        <h2 className="font-heading font-black text-xl text-[#0B1B3A] flex items-center gap-2"><Building2 className="w-5 h-5 text-[#1D4ED8]" /> {form.id ? t("orgs.edit") : t("orgs.new")}</h2>
        <input data-testid="org-name" required placeholder={t("orgs.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-cream w-full" />
        <input data-testid="org-manager" required type="email" placeholder={t("orgs.manager")} value={form.manager_email} onChange={(e) => setForm({ ...form, manager_email: e.target.value })} className="input-cream w-full" />
        <input data-testid="org-seats" type="number" min={1} placeholder={t("orgs.seats")} value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} className="input-cream w-full" />
        <textarea data-testid="org-members" rows={5} placeholder={t("orgs.members")} value={form.member_emails} onChange={(e) => setForm({ ...form, member_emails: e.target.value })} className="input-cream w-full font-mono text-xs" />
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#475569] mb-2">{t("orgs.courses")}</p>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-auto">
            {courses.map((c) => (
              <button type="button" key={c.id} data-testid={`org-course-${c.slug}`} onClick={() => toggleCourse(c.id)}
                className={`text-[11px] font-bold px-3 py-1.5 rounded-full border transition-colors ${form.course_ids.includes(c.id) ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-white text-[#334155] border-[#E2E8F0] hover:border-[#1D4ED8]"}`}>
                {c.title}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="org-save" disabled={saving} className="btn-blue !py-2.5 text-sm"><Save className="w-4 h-4" /> {t("orgs.save")}</button>
          {form.id && <button type="button" onClick={() => setForm(EMPTY)} className="btn-outline-light !py-2.5 text-sm">{t("orgs.cancel")}</button>}
        </div>
      </form>

      <div className="space-y-4" data-testid="org-list">
        {rows === null ? <p className="text-[#64748B]">…</p> : rows.length === 0 ? (
          <div className="card-dark p-10 text-center text-[#64748B]" data-testid="org-empty">{t("orgs.empty")}</div>
        ) : rows.map((o) => (
          <div key={o.id} className="card-dark p-6" data-testid={`org-row-${o.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-heading font-black text-lg text-[#0B1B3A]">{o.name}</h3>
                <p className="text-xs text-[#64748B] mt-1">{t("orgs.manager")}: {o.manager_email} {o.manager_user_id ? "" : `(${t("team.pending")})`}</p>
              </div>
              <div className="flex gap-2">
                <button data-testid={`org-edit-${o.id}`} onClick={() => setForm(toForm(o))} className="btn-outline-light !px-4 !py-2 text-xs">{t("orgs.editBtn")}</button>
                <button data-testid={`org-delete-${o.id}`} onClick={() => remove(o.id)} className="btn-outline-light !px-3 !py-2 text-xs text-[#DC2626]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3 mt-4 text-center">
              {[[`${o.stats.used}/${o.stats.seats}`, t("team.seats")], [o.stats.registered, t("team.registered")], [`${o.stats.avg_progress}%`, t("team.avgProgress")], [o.stats.certificates, t("team.certificates")]].map(([v, l]) => (
                <div key={l} className="rounded-2xl bg-[#F8FAFC] border border-[#E2E8F0] p-3">
                  <p className="font-heading font-black text-lg text-[#0B1B3A]">{v}</p>
                  <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#64748B]">{l}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#475569] mt-3 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {o.courses.map((c) => c.title).join(" · ") || "—"}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrganizationsAdmin;
